import React from 'react';
import { Aushangblatt } from './Aushaenge';
import type { ErsthelferZeile, NotfallStelle } from '../bestandUebernahme';

/**
 * Das Blatt, das die Frage „wohin" beantwortet.
 *
 * ═══ WARUM ES NEBEN DEM NOTFALLBLATT HAENGT UND NICHT DARIN AUFGEHT ═══════
 * „Verhalten im Notfall" beantwortet „was tue ich" — in fuenf Schritten, gross
 * und ohne Anschriften. Dieses Blatt beantwortet „wohin fahre ich" und braucht
 * dafuer Adressen, Entfernungen und den Durchgangsarzt. Beides auf einem Blatt
 * hiesse: im Ernstfall sucht jemand die Handlungsanweisung zwischen Adressen.
 *
 * ═══ EINE QUELLE ══════════════════════════════════════════════════════════
 * Alle Stellen kommen aus dem Notrufplan des SiGe-Plans und werden von dort
 * GELESEN. Der A+S-Plan haelt keine eigene Kopie: eine veraltete Kliniknummer
 * macht im Ernstfall niemanden stutzig, eine fehlende schon.
 *
 * ═══ GEFUNDEN IST NICHT GEPRUEFT ══════════════════════════════════════════
 * Die Marke wird MITGEDRUCKT, statt sie wegzulassen. Eine automatisch
 * gefundene Klinik ist besser als keine — aber wer die Rettungskette plant,
 * muss wissen, welche Zeile ein Mensch angerufen hat.
 *
 * Eigene Datei und nicht in `Aushaenge.tsx`: dort liegen die Blaetter, die
 * allein aus dem A+S-Plan entstehen. Dieses eine liest aus einem fremden
 * Modul, und diese Grenze soll man an der Datei sehen.
 */

const SCHRITTE: [string, string][] = [
    ['Eigene Sicherheit', 'Schutzausrüstung anbehalten, Bereich sichern'],
    ['Notruf 112', 'Wer, wo, was, wie viele, und welche Gefahrstoffe'],
    ['Erste Hilfe', 'Ersthelfer alarmieren, Notfallausweis bereithalten'],
    ['Einweisen', 'Rettungsdienst am Sammelplatz abholen'],
    ['Durchgangsarzt', 'Jeder Arbeitsunfall wird dort vorgestellt'],
];

const KOPFZEILEN = ['Stelle', 'Name', 'Anschrift', 'Telefon', 'Entfernung', 'Stand'];

/**
 * Ohne `luecken`-Prop, und das mit Absicht: die benannten Luecken stehen auf
 * dem Notfallblatt, das direkt daneben haengt. Sie hier zu wiederholen hiesse,
 * dieselbe Aussage zweimal zu drucken — und wer zwei gleiche Kaesten sieht,
 * liest keinen. Was DIESES Blatt betrifft, sagt es selbst: die leere Tabelle
 * und die Marke „nur gefunden" je Zeile.
 */
export const AushangRettungskette: React.FC<{
    projektName: string;
    stellen: NotfallStelle[];
    helfer: ErsthelferZeile[];
    kartenBild?: string;
}> = ({ projektName, stellen, helfer, kartenBild }) => (
    <Aushangblatt
        titel="Rettungskette"
        untertitel={`Wohin im Notfall · ${projektName}`}
        fuss="Quelle ist der Notrufplan des SiGe-Plans; geändert wird er dort, nicht hier. Nach jedem Wechsel des Durchgangsarztes ist dieses Blatt neu zu drucken."
    >
        <ol className="flex gap-2 mb-3">
            {SCHRITTE.map(([kopf, text], i) => (
                <li key={i} className="flex-1 border border-slate-300 rounded-lg p-2">
                    <span className="w-5 h-5 mb-1 rounded-full bg-slate-900 text-white font-black flex items-center justify-center text-[10px]">
                        {i + 1}
                    </span>
                    <b className="block text-[11px] leading-tight text-slate-900">{kopf}</b>
                    <span className="block text-[10px] leading-tight text-slate-600">{text}</span>
                </li>
            ))}
        </ol>

        {stellen.length > 0
            ? (
                <table className="w-full border-collapse mb-3">
                    <thead>
                        <tr>
                            {KOPFZEILEN.map(k => (
                                <th
                                    key={k}
                                    className="border border-slate-400 bg-slate-100 px-1.5 py-1 text-left
                                               text-[9px] font-black uppercase tracking-wide text-slate-700"
                                >
                                    {k}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {stellen.map(s => (
                            <tr key={s.key}>
                                <td className="border border-slate-300 px-1.5 py-1 text-[11px] font-bold text-slate-900">
                                    {s.was}
                                </td>
                                <td className="border border-slate-300 px-1.5 py-1 text-[11px] text-slate-800">
                                    {s.name || <span className="text-amber-700 font-bold">ohne Namen</span>}
                                    {s.hinweis && (
                                        <span className="block text-[9px] text-amber-800 leading-tight">
                                            {s.hinweis}
                                        </span>
                                    )}
                                </td>
                                <td className="border border-slate-300 px-1.5 py-1 text-[10px] text-slate-700">
                                    {s.adresse}
                                </td>
                                <td className="border border-slate-300 px-1.5 py-1 text-[12px] font-black text-slate-900 whitespace-nowrap">
                                    {s.telefon
                                        ? s.telefon
                                        : <span className="text-[10px] font-bold text-amber-700">nicht hinterlegt</span>}
                                </td>
                                <td className="border border-slate-300 px-1.5 py-1 text-[10px] text-slate-700 whitespace-nowrap">
                                    {s.entfernung}
                                </td>
                                <td className="border border-slate-300 px-1.5 py-1 text-[9px] font-bold whitespace-nowrap">
                                    {s.geprueft
                                        ? <span className="text-emerald-700">bestätigt</span>
                                        : <span className="text-slate-500">nur gefunden</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
            : (
                <div className="mb-3 border-2 border-amber-500 rounded-lg p-2.5">
                    <p className="text-sm font-bold text-amber-900">
                        Im Notrufplan des SiGe-Plans ist keine Notfallstelle hinterlegt.
                    </p>
                    <p className="text-xs text-amber-900">
                        Bis dahin gilt allein die 112. Klinik und Durchgangsarzt werden im
                        Notrufplan des SiGe-Plans festgelegt; dieses Blatt liest sie nur.
                    </p>
                </div>
            )}

        <div className="grid grid-cols-2 gap-4">
            <div>
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                    Ersthelfer auf dieser Baustelle
                </p>
                {helfer.length > 0
                    ? (
                        <ul className="text-sm text-slate-800 leading-tight">
                            {helfer.map((h, i) => (
                                <li key={i}>
                                    · {h.name}
                                    {h.firma && <span className="text-slate-500">, {h.firma}</span>}
                                </li>
                            ))}
                        </ul>
                    )
                    : (
                        <p className="text-sm font-bold text-amber-700">
                            Im Notrufplan ist kein Ersthelfer benannt.
                        </p>
                    )}
            </div>
            {kartenBild && (
                <img
                    src={kartenBild}
                    alt="Lageplan mit den Notfallstellen"
                    className="w-full object-contain border border-slate-300 rounded"
                    style={{ maxHeight: 220 }}
                />
            )}
        </div>
    </Aushangblatt>
);
